import { UserDetailsContext } from 'context/userDetailsContext';
import { useCallback, useEffect, useContext, useMemo } from 'react';
import { getMessages } from 'services/messages/messageService';
import { getTenantIdFromLS, getTenantSkFromLS } from 'utils/localStorageUtils';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../config/queryClient';

export const useSecureMessages = ({
  case_id,
  isLeadSpecific = false,
  isFromCaseSpecificModule = false,
  isGlobalMessagesScreen = false,
  page_limit = 100,
  resolution_status = false,
  options = {},
}) => {
  const { isCase = false, isLeads = false } = options;
  const userContext = useContext(UserDetailsContext);
  const queryClient = useQueryClient();
  const tenantId = getTenantIdFromLS();
  const tenant_sk = getTenantSkFromLS();
  const tenantSk = tenant_sk ? tenant_sk.replaceAll('#', '%23') : '';

  function getPageLimit() {
    if (isLeadSpecific && userContext) {
      return userContext?.firmDetails?.pagination_preference?.leads?.message;
    } else if (isFromCaseSpecificModule && userContext) {
      return userContext?.firmDetails?.pagination_preference?.case?.message;
    } else if (isGlobalMessagesScreen && userContext) {
      return userContext?.firmDetails?.pagination_preference?.messages?.self;
    }
    return page_limit;
  }

  const pageLimit = getPageLimit();

  // Build query key with all parameters for proper caching
  const queryKey = useMemo(() => {
    const params = {
      case_id,
      is_global: isGlobalMessagesScreen,
      pageLimit,
      is_read: '',
      author_id: '',
      resolution_status,
      tenantId,
      tenantSk,
      isCase,
      isLeads,
      isLeadSpecific,
      isFromCaseSpecificModule,
    };
    return queryKeys.secureMessages.list(params);
  }, [
    case_id,
    isGlobalMessagesScreen,
    pageLimit,
    resolution_status,
    tenantId,
    tenantSk,
    isCase,
    isLeads,
    isLeadSpecific,
    isFromCaseSpecificModule,
  ]);

  // React Query hook
  const { data, isLoading, error, refetch } = useQuery({
    queryKey,
    queryFn: async () => {
      const obj = {
        case_id,
        is_global: isGlobalMessagesScreen ? true : false,
        pageLimit,
        tenantId,
        tenantSk,
        is_read: '',
        author_id: '',
        resolution_status: resolution_status ?? false,
      };
      const response = await getMessages(obj);
      return response.data?.message || [];
    },
    staleTime: isCase || isLeads ? 5 * 60 * 1000 : 2 * 60 * 1000, // 5 minutes for cases/leads, 2 minutes otherwise
    gcTime: isCase || isLeads ? 30 * 60 * 1000 : 10 * 60 * 1000, // 30 minutes for cases/leads, 10 minutes otherwise
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    enabled: !!(case_id || isGlobalMessagesScreen) && isLeadSpecific,
  });

  const loadCaseSecureMessages = useCallback(
    async (obj = {}) => {
      // Build new params with updated values
      const newParams = {
        case_id: obj.case_id ?? case_id,
        is_global: obj.is_global ?? (isGlobalMessagesScreen ? true : false),
        pageLimit: obj.pageLimit ?? pageLimit,
        tenantId,
        tenantSk,
        is_read: obj?.is_read ?? '',
        author_id: obj?.author_id ?? '',
        resolution_status: obj?.resolution_status ?? resolution_status ?? false,
        isCase,
        isLeads,
        isLeadSpecific,
        isFromCaseSpecificModule,
      };

      // Invalidate and refetch with new params
      const newQueryKey = queryKeys.secureMessages.list(newParams);
      queryClient.invalidateQueries({ queryKey: newQueryKey });
      await refetch();
    },
    [
      case_id,
      isGlobalMessagesScreen,
      pageLimit,
      tenantId,
      tenantSk,
      resolution_status,
      isCase,
      isLeads,
      isLeadSpecific,
      isFromCaseSpecificModule,
      queryClient,
      refetch,
    ]
  );

  useEffect(() => {
    if (case_id || isGlobalMessagesScreen) {
      // Query will automatically fetch when enabled
    }
  }, [case_id, isGlobalMessagesScreen]);

  return {
    loadCaseSecureMessages,
    caseSecureMessages: data || [],
    loading: isLoading,
    error,
  };
};
